import React from 'react';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { localMonthGraph } from 'lib/api/localreport';

import CurrentStatus from 'components/atoms/CurrentStatus';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ContentsWrap from 'components/molecules/ContentsWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('월별 소진'),
    data: { bar: ['ADVRTS_AMT'] },
    yAxisData: {
      left: {
        ADVRTS_AMT: true,
      },
      right: {},
    },
  },
  {
    title: i18next.t('월별 CTR'),
    data: { bar: ['CLICK_RATE'] },
    yAxisData: {
      left: {
        CLICK_RATE: true,
        isPercent: true,
      },
      right: {},
    },
    customizeData: {
      CLICK_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
      },
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      graphComponent: {},
      infoMessage: '',
      loading: false,
      error: false,
      noData: false,
    };
  }

  getData = () => {
    const { params, locale, t, handleGraphLoading } = this.props;
    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    localMonthGraph({ ...paramConverter(params) })
      .then((response) => {
        const { data } = response.data;
        if (data.length === 0) {
          this.setState({
            ...this.state,
            noData: true,
            loading: false,
          });
        } else if (typeof data !== 'undefined') {
          this.setState({
            ...this.state,
            primitiveData: data,
            loading: false,
            error: false,
            noData: false,
          });

          for (let i = 0; i <= 1; i++) {
            this.setGraphData(graphList[i], i)();
          }
        }
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
        });
      })
      .finally(() => {
        handleGraphLoading(false);
      });
  };

  componentDidMount() {
    const { handleGraphLoading } = this.props;
    handleGraphLoading(true);
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params, handleGraphLoading } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      handleGraphLoading(true);
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    const { handleGraphLoading } = this.props;
    handleGraphLoading(true);
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t } = this.props;
    const dateObj = moment(originalDate, 'YYYYMM');
    const month = twoDigits(dateObj.month() + 1);
    const year = twoDigits(dateObj.year());

    return onlyDay ? (
      `${t(month + '월')}`
    ) : (
      <span>
        {`${t(year + '년')}`}
        {`${t(month + '월')}`}
      </span>
    );
  };

  xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    const dayStr = this.makeLabel(true)(value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="middle">
          {dayStr}
        </text>
      </g>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  setGraphData =
    (
      { title, data, yAxisData, customizeData: customize, isPercent = false },
      index
    ) =>
    () => {
      const { primitiveData } = this.state;
      if (typeof data !== 'undefined') {
        const { bar, line } = data;
        const dataKeys = {};
        const keyLabel = {};
        const tempGraphData = {};
        const graphData = [];
        let customizeData = {};
        const stackId = [];

        if (typeof bar !== 'undefined') {
          dataKeys['bar'] = bar;
        }

        if (typeof line !== 'undefined') {
          dataKeys['line'] = line;
        }

        if (typeof customize !== 'undefined') {
          customizeData = customize;
        }

        for (let kind in data) {
          if (data.hasOwnProperty(kind)) {
            const keys = data[kind];
            for (let i = 0; i < keys.length; i++) {
              const curKey = keys[i];
              if (kind === 'bar') {
                stackId.push(curKey);
              }

              keyLabel[curKey] = primitiveData[curKey].title;
              tempGraphData[curKey] = primitiveData[curKey].data;
            }
          }
        }

        // graph data 담는 곳
        for (let key in tempGraphData) {
          if (tempGraphData.hasOwnProperty(key)) {
            for (let i = 0; i < tempGraphData[key].length; i++) {
              const curGraphData = tempGraphData[key][i];
              let { name, val } = curGraphData;
              if (
                typeof customize !== 'undefined' &&
                typeof customize[key] !== 'undefined'
              ) {
                if (customize[key].isPercent) {
                  val *= 100;
                }
              }

              graphData[i] = {
                ...graphData[i],
                name,
                [key]: val,
              };
            }
          }
        }

        const result = (
          <MultiplexChart
            xInterval={0}
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            useTitle
          />
        );

        const { graphComponent } = this.state;
        graphComponent[index] = result;

        this.setState({
          ...this.state,
          graphComponent: graphComponent,
        });
      }
    };

  // const isLoading = this.state.list === null && this.state.error !== true;
  render() {
    const { handleRefesh } = this;
    const { t } = this.props;
    const { infoMessage, graphComponent, loading, error, noData } = this.state;

    const graphIndexs = [0, 1];

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('월별통계 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || noData || error ? (
          <CurrentStatus
            type={loading ? 'loading' : noData ? 'noData' : error && 'error'}
          />
        ) : (
          <ContentsWrap contentsCount={2}>
            {graphIndexs.map((index) => {
              if (typeof graphComponent[index] === 'undefined') return null;
              return (
                <div key={index}>
                  {typeof graphComponent[index] !== 'undefined'
                    ? graphComponent[index]
                    : ''}
                </div>
              );
            })}
          </ContentsWrap>
        )}
      </SwitchingArea>
    );
  }
}

export default withTranslation()(
  connect(
    ({ locale }) => ({
      locale: locale.locale,
    }),
    null
  )(withTheme(StatisticsGraph))
);
